import { isCoachingRole, type CoachingRole } from "../domain/roles";
import { ApiClientError } from "./api-client";
import type { CoachingHistoryEventDto, CoachingOperationResult, CoachingUserDto, ListResponse } from "./coaching-dtos";
type HistoryAppender = (event: CoachingHistoryEventDto) => void;

export function createMockUserLifecycleStore(
  initialUsers: CoachingUserDto[],
  appendHistory: HistoryAppender,
  getActorRole: () => string | undefined = () => "admin"
) {
  const users = initialUsers.map(snapshotItem);
  const createIdempotency = new Map<string, CoachingUserDto>();
  return {
    list: (query?: URLSearchParams): ListResponse<CoachingUserDto> => {
      const filtered = filterUsers(users, query);
      const sorted = sortUsers(filtered, query?.get("sort"));
      const page = positiveInt(query?.get("page"), 1);
      const pageSize = boundedPageSize(query?.get("per_page") ?? query?.get("limit"));
      const offset = (page - 1) * pageSize;
      const items = sorted.slice(offset, offset + pageSize).map(snapshotItem);
      return {
        items,
        next_cursor: null,
        page,
        pageSize,
        total: sorted.length,
        hasNext: offset + items.length < sorted.length
      };
    },
    find: (userId: string) => findMockUser(users, userId),
    create: (body: Record<string, unknown>, idempotencyKey?: string) => {
      if (idempotencyKey && createIdempotency.has(idempotencyKey)) {
        return snapshotItem(createIdempotency.get(idempotencyKey)!);
      }
      rejectElevatedRoleGrant(body, appendHistory, getActorRole, idempotencyKey);
      const user: CoachingUserDto = {
        id: `user-${users.length + 1}`,
        email: stringField(body.email),
        name: stringField(body.name) || stringField(body.email) || "Coaching user",
        role: roleField(body.role) || "coach",
        status: mockUserStatus(body, "invited"),
        company_id: stringField(body.company_id),
        program_id: stringField(body.program_id),
        cohort_id: stringField(body.cohort_id),
        scope_label: stringField(body.scope_label)
      };
      users.push(user);
      appendHistory({
        id: `mock-user-created-${user.id}`,
        action: "users.create",
        actor_id: stringField(body.actor_id) || "mock-admin-1",
        summary: `Created user ${user.id}`
      });
      if (idempotencyKey) createIdempotency.set(idempotencyKey, user);
      return snapshotItem(user);
    },
    update: (userId: string, body: Record<string, unknown>, idempotencyKey?: string) => {
      const index = users.findIndex((user) => user.id === userId);
      if (index === -1) throw new ApiClientError("User not found", 404, "mock-user-not-found");
      rejectElevatedRoleGrant(body, appendHistory, getActorRole, idempotencyKey, users[index].role);
      users[index] = {
        ...users[index],
        email: stringField(body.email) || users[index].email,
        name: stringField(body.name) || users[index].name,
        role: roleField(body.role) || users[index].role,
        status: mockUserStatus(body, users[index].status),
        company_id: stringField(body.company_id) || users[index].company_id,
        program_id: stringField(body.program_id) || users[index].program_id,
        cohort_id: stringField(body.cohort_id) || users[index].cohort_id,
        scope_label: stringField(body.scope_label) || users[index].scope_label
      };
      appendHistory({
        id: `mock-user-updated-${userId}-${supportHash(idempotencyKey ?? "no-ref")}`,
        action: "users.update",
        actor_id: stringField(body.actor_id) || "mock-admin-1",
        summary: `Updated user ${userId}`
      });
      return snapshotItem(users[index]);
    },
    deactivate: (userId: string, idempotencyKey?: string): CoachingOperationResult => {
      const index = users.findIndex((user) => user.id === userId);
      if (index === -1) throw new ApiClientError("User not found", 404, "mock-user-not-found");

      users[index] = { ...users[index], status: "inactive" };
      appendHistory({
        id: `mock-user-deactivated-${userId}-${supportHash(idempotencyKey ?? "no-ref")}`,
        action: "users.deactivate",
        actor_id: "mock-admin-1",
        summary: `Deactivated user ${userId}`
      });
      return {
        ok: true,
        id: userId,
        reference: idempotencyKey ?? `mock:/users/${userId}/deactivate`,
        status: "inactive",
        side_effects: ["db1-write", "history-append"]
      };
    }
  };
}

function filterUsers(users: CoachingUserDto[], query?: URLSearchParams): CoachingUserDto[] {
  if (!query) return users.filter((user) => user.status !== "inactive");
  const id = query.get("id");
  const role = query.get("role");
  const status = query.get("status");
  const companyId = query.get("company_id");
  const programId = query.get("program_id");
  const cohortId = query.get("cohort_id");
  const q = query.get("q")?.toLowerCase();
  const includeDeactivated = query.get("include_deactivated") === "true" || query.get("includeDeactivated") === "true" || status === "inactive";

  return users.filter((user) => {
    if (!includeDeactivated && user.status === "inactive") return false;
    if (id && user.id !== id) return false;
    if (role && user.role !== role) return false;
    if (status && user.status !== status) return false;
    if (companyId && user.company_id !== companyId) return false;
    if (programId && user.program_id !== programId) return false;
    if (cohortId && user.cohort_id !== cohortId) return false;
    if (q && !userSearchText(user).includes(q)) return false;
    return true;
  });
}
type MockUserSortField = "name" | "email" | "role" | "status" | "created";

function sortUsers(users: CoachingUserDto[], value: string | null | undefined): CoachingUserDto[] {
  const [rawField, rawDirection = "asc"] = (value ?? "name:asc").split(":", 2);
  const fields: MockUserSortField[] = ["name", "email", "role", "status", "created"];
  const field = fields.includes(rawField as MockUserSortField) ? rawField as MockUserSortField : "name";
  const direction = rawDirection === "desc" ? -1 : 1;
  return [...users].sort((left, right) => {
    const leftValue = sortValue(left, field);
    const rightValue = sortValue(right, field);
    // Mock ids are creation-ordered ("user-2" before "user-10"), so compare numerically.
    const primary = leftValue.localeCompare(rightValue, undefined, { numeric: true });
    return primary === 0 ? left.id.localeCompare(right.id, undefined, { numeric: true }) : primary * direction;
  });
}

function sortValue(user: CoachingUserDto, field: MockUserSortField): string {
  if (field === "email") return user.email ?? "";
  if (field === "role") return user.role ?? "";
  if (field === "status") return user.status ?? "";
  return field === "created" ? user.id : user.name ?? user.email ?? user.id;
}

function positiveInt(value: string | null | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function boundedPageSize(value: string | null | undefined): number {
  return Math.min(positiveInt(value, 100), 100);
}

function userSearchText(user: CoachingUserDto) {
  return [user.id, user.email, user.name, user.role, user.status, user.company_id, user.program_id, user.cohort_id, user.scope_label]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}
function mockUserStatus(body: Record<string, unknown>, fallback: string | undefined): string {
  if (typeof body.active === "boolean") return body.active ? "active" : "suspended";
  const status = stringField(body.status);
  return status ?? fallback ?? "active";
}

function findMockUser(users: CoachingUserDto[], userId: string): CoachingUserDto {
  const user = users.find((item) => item.id === userId);
  if (!user) throw new ApiClientError("User not found", 404, "mock-user-not-found");
  return snapshotItem(user);
}

function rejectElevatedRoleGrant(
  body: Record<string, unknown>,
  appendHistory: HistoryAppender,
  getActorRole: () => string | undefined,
  idempotencyKey?: string,
  currentRole?: CoachingRole
) {
  const requestedRole = stringField(body.role);
  if (requestedRole == null || requestedRole === currentRole) return;
  const actorRole = stringField(getActorRole()) || "admin";
  if ((requestedRole === "admin" || requestedRole === "super_admin" || currentRole === "admin" || currentRole === "super_admin") && actorRole !== "super_admin") {
    appendHistory({
      id: `denied-elevated-role-${supportHash(idempotencyKey ?? requestedRole)}`,
      action: "mutation.denied",
      actor_id: stringField(body.actor_id) || "mock-admin-1",
      summary: "admin denied for users:elevated-role-grant"
    });
    throw new ApiClientError("Elevated role grant denied", 403, idempotencyKey ?? "mock-user-elevated-grant-denied");
  }
}

function roleField(value: unknown): CoachingRole | undefined {
  const role = stringField(value);
  return isCoachingRole(role) ? role : undefined;
}

function stringField(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function snapshotItem<T>(item: T): T {
  if (item && typeof item === "object" && !Array.isArray(item)) return { ...item };
  return item;
}

function supportHash(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
  }
  return Math.abs(hash).toString(36);
}
